import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { Illustration, type IllustrationName } from 'components/common/illustration';
import { typeStyle } from 'constants/typography';

type EmptyStateLayoutProps = {
  'data-e2e'?: string;
  icon?: React.ReactNode;
  illustration?: IllustrationName;
  titleId?: string;
  description: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
};

// `icon` and `titleId` are optional so EmptyContent can delegate here: its 35 call sites carry a
// single sentence and no title. Figma specs no title-less variant - extrapolated per D1.
const EmptyStateLayout: React.FunctionComponent<EmptyStateLayoutProps> = ({
  'data-e2e': dataE2e,
  icon,
  illustration,
  titleId,
  description,
  action,
  className
}) => (
  <CenteredContainer data-e2e={dataE2e} className={className}>
    {illustration ? (
      <Illustration name={illustration} data-e2e={`${dataE2e}-illustration`} />
    ) : icon ? (
      <LargeIconContainer>{icon}</LargeIconContainer>
    ) : null}
    {titleId ? (
      <ColoredSubtitle>
        <FormattedMessage id={titleId} />
      </ColoredSubtitle>
    ) : null}
    <DescriptionText>{description}</DescriptionText>
    {action}
  </CenteredContainer>
);

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  gap: var(--spacing-xs);
  padding: var(--padding-xxxl);
`;

const LargeIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: var(--scale-800);
    color: var(--icon-secondary);
  }
`;

const ColoredSubtitle = styled.h3`
  ${typeStyle.headingH4}
  text-align: center;
  color: var(--text-heading);
  margin: 0;
`;

const DescriptionText = styled.div`
  ${typeStyle.bodyMd}
  text-align: center;
  color: var(--text-secondary);
  white-space: pre-line;
`;

export default EmptyStateLayout;
